import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import StarRating from "@/components/StarRating";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const LAYOUTS = [
  { id: "classic", name: "Classic", desc: "Walls on the flanks, cuts spread wide" },
  { id: "gauntlet", name: "Gauntlet", desc: "Funnel through the centre" },
  { id: "open_field", name: "Open Field", desc: "No walls. Cuts decide" },
  { id: "fortress", name: "Classic Fortress", desc: "Heavy walls, cut-throughs essential" },
  { id: "mini", name: "Mini", desc: "Compact 12-row board, 10 tiles" },
  { id: "boardgame", name: "Original", desc: "31×15 board, square bases, POW cards" },
  { id: "boardgame2", name: "Fortress", desc: "Original with Shield squares" },
  { id: "fortress2", name: "Fortress 2.0", desc: "Fortress, no grey POWs, 10 tiles" },
  { id: "infinity2", name: "Infinity", desc: "Fortress 2.0, centre and corner walls, sides joined" },
  { id: "boardgame3", name: "Classic", desc: "No gems" },
  { id: "firefly2", name: "Firefly", desc: "33×15 board, gems, starting wall tiles" },
  { id: "boardgame5", name: "The Wave", desc: "33×15 board, gems, wedges of wall tiles" },
  { id: "bridges2", name: "Bridges", desc: "13×31 board, a river and bridge tiles" },
  { id: "bridges3", name: "Bridges 2.0", desc: "Bridges with sand each side of the river" },
];

export default function FeedbackModal({ open, onOpenChange, matchId, defaultLayout = "classic", onDone }) {
  const [layout, setLayout] = useState(defaultLayout);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!rating) {
      toast.error("Please select a star rating");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/match/${matchId}/feedback`, { layout, rating, comment });
      toast.success("Thanks for your feedback!");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-surface border-border p-0 overflow-hidden" data-testid="feedback-modal">
        <div className="p-8">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">How was that match?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Help us tune Jangle by rating the board layout you played.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 space-y-6">
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Which layout did you prefer?
              </Label>
              <div className="grid grid-cols-2 gap-2.5">
                {LAYOUTS.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => setLayout(l.id)}
                    className={`text-left p-3.5 rounded-xl border transition-colors duration-200 ${
                      layout === l.id
                        ? "border-brand bg-brand/10"
                        : "border-border bg-background hover:border-foreground/40"
                    }`}
                    data-testid={`feedback-layout-${l.id}`}
                  >
                    <div className="font-display font-bold text-sm">{l.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{l.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Rate your experience
              </Label>
              <StarRating value={rating} onChange={setRating} testid="feedback-rating" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="comment" className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Comments (optional)
              </Label>
              <Textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What did you like or want to see changed?"
                maxLength={500}
                className="bg-background min-h-[88px] resize-none"
                data-testid="feedback-comment-input"
              />
              <div className="text-xs text-muted-foreground text-right">{comment.length}/500</div>
            </div>
          </div>
        </div>

        <div className="bg-background border-t border-border px-8 py-4 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="feedback-skip-btn"
          >
            Skip
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-brand hover:bg-brand-hover text-brand-foreground rounded-full px-6"
            data-testid="feedback-submit-btn"
          >
            {submitting ? "Submitting…" : "Submit feedback"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
